import React, { useCallback, useMemo, useState } from 'react'
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { Icon } from '@iconify/react'
import styled from 'styled-components'
import {
  MilestoneDetail,
  MilestoneStatus,
} from '../../../../../../../../domain/entity/MilestoneEntity'
import { colorPalette } from '../../../../../../../style/colorPalette'
import BigFireIcon from '../../../../../../../components/icons/BigFireIcon'
import { intl } from '../../../../../../../../i18n'

export type MilestoneWithPosition = {
  milestone: MilestoneDetail
  position?: number
}

const Badge = styled.span`
  position: absolute;
  top: -3px;
  right: -2px;
  font-size: 7px;
  font-weight: bold;
  color: ${colorPalette.monotone[10]};
  background-color: ${colorPalette.monotone[1]};
  border-radius: 50%;
  width: 9px;
  height: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`

type IconContainerProps = {
  $opacity?: number
}

const IconContainer = styled.span<IconContainerProps>`
  display: flex;
  align-items: center;
  position: relative;
  line-height: 1;
  opacity: ${({ $opacity }) => $opacity ?? 1};
`

const PopoverContent = styled.div`
  padding: 8px 12px;
`

const PopoverItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const PopoverItemWrapper = styled.div<{ $marginTop?: number }>`
  margin-top: ${({ $marginTop }) => $marginTop ?? 0}px;
`

const ClickableSpan = styled.span`
  cursor: pointer;
`

type PositionedMilestoneIconProps = {
  $position: number
}

const PositionedMilestoneIcon = styled.span<PositionedMilestoneIconProps>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  cursor: pointer;
`

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.green[5]};
`

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.monotone[4]};
`

const DateTypography = styled(Typography)`
  font-size: 11px;
  color: ${colorPalette.monotone[5]};
  white-space: nowrap;
`

const NameTypography = styled(Typography)`
  font-size: 12px;
  flex: 1;
  min-width: 0;
`

const StyledIconButton = styled(IconButton)`
  padding: 2px;
`

const StyledEditIcon = styled(EditIcon)`
  font-size: 14px;
  color: ${colorPalette.monotone[5]};
`

const AddMilestoneContainer = styled(Box)`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${colorPalette.monotone[2]};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: -12px;
  margin-right: -12px;
  margin-bottom: -8px;
  padding-left: 12px;
  padding-right: 12px;
  padding-bottom: 6px;
  cursor: pointer;
  color: ${colorPalette.skyBlue[7]};
  border-radius: 4px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colorPalette.monotone[1]};
  }
`

const StyledAddIcon = styled(AddIcon)`
  font-size: 14px;
`

const AddTypography = styled(Typography)`
  font-size: 12px;
`

const EmptyCellContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const ButtonWrapper = styled(Box)`
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${colorPalette.skyBlue[1]};
  color: ${colorPalette.skyBlue[7]};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${colorPalette.skyBlue[2]};
  }
`

const SmallAddIcon = styled(AddIcon)`
  font-size: 12px;
`

const popoverProps = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  sx: { transform: 'translateX(-6px)' },
}

export const getMilestoneStatus = (
  milestone: MilestoneDetail
): MilestoneStatus => {
  if (milestone.achieved) return 'achieved'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const milestoneDate = new Date(milestone.milestoneDate)
  if (milestoneDate < today) return 'overdue'
  return 'pending'
}

const formatMilestoneDate = (dateString: string): string => {
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

const StatusIcon = ({ milestone }: { milestone: MilestoneDetail }) => {
  const status = getMilestoneStatus(milestone)

  if (status === 'achieved') {
    return <StyledCheckCircleIcon />
  }
  if (status === 'overdue') {
    return <BigFireIcon style={{ fontSize: 14, flexShrink: 0 }} />
  }
  return <StyledCheckCircleOutlineIcon />
}

const MilestoneIconWithBadge = ({
  milestone,
  count,
  opacity,
}: {
  milestone: MilestoneDetail
  count: number
  opacity: number
}) => (
  <IconContainer $opacity={opacity}>
    <Icon
      icon={milestone.iconName ?? 'mdi:cards-diamond'}
      width={16}
      height={16}
      color={milestone.color}
    />
    {count > 1 && <Badge>{count}</Badge>}
  </IconContainer>
)

type MilestonePopoverContentProps = {
  milestones: MilestoneDetail[]
  showDate?: boolean
  onEdit?: (uuid: string) => void
  onAdd?: () => void
}

const MilestonePopoverItem = ({
  milestone,
  showDate,
  onEdit,
}: {
  milestone: MilestoneDetail
  showDate?: boolean
  onEdit?: (uuid: string) => void
}) => (
  <PopoverItem>
    <Icon
      icon={milestone.iconName ?? 'mdi:cards-diamond'}
      width={14}
      height={14}
      color={milestone.color}
    />
    {showDate && (
      <DateTypography variant="body2">
        {formatMilestoneDate(milestone.milestoneDate)}
      </DateTypography>
    )}
    <NameTypography variant="body2">{milestone.name}</NameTypography>
    <StatusIcon milestone={milestone} />
    {onEdit && (
      <StyledIconButton
        size="small"
        onClick={e => {
          e.stopPropagation()
          onEdit(milestone.uuid)
        }}
      >
        <StyledEditIcon />
      </StyledIconButton>
    )}
  </PopoverItem>
)

const AddMilestoneButton = ({ onAdd }: { onAdd: () => void }) => (
  <AddMilestoneContainer
    onClick={e => {
      e.stopPropagation()
      onAdd()
    }}
  >
    <StyledAddIcon />
    <AddTypography variant="body2">
      {intl.formatMessage({ id: 'milestone.popover.add' })}
    </AddTypography>
  </AddMilestoneContainer>
)

const MilestonePopoverContent = ({
  milestones,
  showDate = false,
  onEdit,
  onAdd,
}: MilestonePopoverContentProps) => (
  <PopoverContent>
    {milestones.map((milestone, index) => (
      <PopoverItemWrapper key={milestone.uuid} $marginTop={index > 0 ? 4 : 0}>
        <MilestonePopoverItem
          milestone={milestone}
          showDate={showDate}
          onEdit={onEdit}
        />
      </PopoverItemWrapper>
    ))}
    {onAdd && <AddMilestoneButton onAdd={onAdd} />}
  </PopoverContent>
)

export const MilestoneIcons = ({
  milestones,
  positioned = false,
  onEdit,
  onAdd,
}: {
  milestones: MilestoneWithPosition[]
  positioned?: boolean
  onEdit?: (uuid: string) => void
  onAdd?: () => void
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleEdit = useCallback(
    (uuid: string) => {
      setAnchorEl(null)
      onEdit?.(uuid)
    },
    [onEdit]
  )

  const handleAdd = useCallback(() => {
    setAnchorEl(null)
    onAdd?.()
  }, [onAdd])

  const open = Boolean(anchorEl)

  const allMilestones = milestones.map(item => item.milestone)
  const firstMilestone = milestones[0].milestone
  const allAchieved = allMilestones.every(milestone => milestone.achieved)

  const groupedByPosition = useMemo(() => {
    if (!positioned) return []
    const groups = new Map<number, MilestoneDetail[]>()
    for (const { milestone, position } of milestones) {
      const pos = position ?? 50
      if (!groups.has(pos)) {
        groups.set(pos, [])
      }
      groups.get(pos)!.push(milestone)
    }
    return Array.from(groups.entries()).map(([pos, items]) => ({
      position: pos,
      milestones: items,
    }))
  }, [milestones, positioned])

  const popover = (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      {...popoverProps}
    >
      <MilestonePopoverContent
        milestones={allMilestones}
        showDate={positioned}
        onEdit={onEdit ? handleEdit : undefined}
        onAdd={onAdd ? handleAdd : undefined}
      />
    </Popover>
  )

  if (!positioned) {
    return (
      <>
        <ClickableSpan onClick={handleClick}>
          <MilestoneIconWithBadge
            milestone={firstMilestone}
            count={milestones.length}
            opacity={allAchieved ? 0.4 : 1}
          />
        </ClickableSpan>
        {popover}
      </>
    )
  }

  return (
    <>
      {groupedByPosition.map(({ position, milestones: groupMilestones }) => {
        const groupFirstMilestone = groupMilestones[0]
        const groupAllAchieved = groupMilestones.every(
          milestone => milestone.achieved
        )
        return (
          <PositionedMilestoneIcon
            key={`pos-${position}`}
            onClick={handleClick}
            $position={position}
          >
            <MilestoneIconWithBadge
              milestone={groupFirstMilestone}
              count={groupMilestones.length}
              opacity={groupAllAchieved ? 0.4 : 1}
            />
          </PositionedMilestoneIcon>
        )
      })}
      {popover}
    </>
  )
}

export const EmptyMilestoneCell = ({ onAdd }: { onAdd: () => void }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <EmptyCellContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <ButtonWrapper>
          <Tooltip
            arrow={true}
            title={intl.formatMessage({ id: 'milestone.popover.add' })}
          >
            <AddButton
              onClick={e => {
                e.stopPropagation()
                onAdd()
              }}
            >
              <SmallAddIcon />
            </AddButton>
          </Tooltip>
        </ButtonWrapper>
      )}
    </EmptyCellContainer>
  )
}
